import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindowF,
  CircleF,
} from '@react-google-maps/api';
import Loader from './Loader';

const containerStyle = {
  position: 'relative',
  height: '600px',
};

const hoverDistanceStyle = {
  position: 'absolute',
  top: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '8px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  borderRadius: '4px',
  zIndex: 1000,
  fontSize: '14px',
  pointerEvents: 'none',
};

let distanceInMeters = 0;

const formatDistance = (p1, p2) => {
  if (!window.google?.maps?.geometry?.spherical) return null;
  distanceInMeters =
    window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);

  if (distanceInMeters >= 1000) {
    const distanceInKm = distanceInMeters / 1000;
    return `${distanceInKm.toFixed(2)}km*`;
  } else {
    return `${Math.round(distanceInMeters)}m*`;
  }
};

function GoogleMapComponent({center, zoom, multipleMarkers, customStyles}) {
  const [activeMarker, setActiveMarker] = useState(null);
  const [hoverDistance, setHoverDistance] = useState(null);

  const {isLoaded, loadError} = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAP_API_KEY ||
      'AIzaSyAM1hg9be5WiIXnupnuM5BBicdXnMDaxRw',
    libraries: ['geometry'],
  });

  const originLatLng = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    const fixedP1 = multipleMarkers?.length > 0 ? multipleMarkers[0] : center;

    if (!fixedP1) return null;

    return new window.google.maps.LatLng(fixedP1.lat, fixedP1.lng);
  }, [multipleMarkers, center, isLoaded]);

  const handleActiveMarker = useCallback(
    marker => {
      if (marker === activeMarker) return setActiveMarker(null);
      setActiveMarker(marker);
    },
    [activeMarker],
  );

  const handleMarkerMouseOver = useCallback(
    marker => {
      if (!originLatLng) return;

      // P2 is the position of the marker being hovered over
      const markerLatLng = new window.google.maps.LatLng(
        marker.lat,
        marker.lng,
      );

      const distanceString = formatDistance(originLatLng, markerLatLng);
      setHoverDistance(distanceString);
    },
    [originLatLng],
  );

  const handleMarkerMouseOut = useCallback(() => {
    setHoverDistance(null);
  }, []);

  useEffect(() => {
    if (multipleMarkers && multipleMarkers.length > 0) {
      setActiveMarker(multipleMarkers[0]);
    }
  }, [multipleMarkers]);

  const radiusColors = distanceInMeters > 50 ? '#ef6820' : '#306EEF';
  const headerContent = useMemo(() => {
    const div = document.createElement('div');
    div.innerHTML = `
      <h3 style="margin: 0;
          font-size: 16px;
          font-weight: bold;
          color: #212121;"
      >
          ${activeMarker?.name}
      </h3>
          
    `;
    return div;
  }, [activeMarker]);
  const content = useMemo(() => {
    const div = document.createElement('div');
    div.innerHTML = `
      <p style="margin-top: 1px ; font-size: 14px; color: #757575;">
          Lat: <span style="font-weight: normal; color: #4E4E4E">${
            activeMarker?.lat
          }</span>
      </p>
      <p style=" font-size: 14px; color: #757575; margin-top: 6px">
        Lon: <span style="font-weight: normal; color: #4E4E4E">${
          activeMarker?.lng
        }</span>
      </p>
      <div style="height: 1px; width: 100%; background: #dedede; margin: 8px 0"></div>
      <a
        href="${`https://www.google.com/maps/search/?api=1&query=${activeMarker?.lat},${activeMarker?.lng}`}"
        target="_blank"
        rel="noopener noreferrer"
        style="color: #1a73e8; text-decoration: none; font-size: 14px;"
      >
        View on Google Maps
      </a>
          
    `;
    return div;
  }, [activeMarker]);

  const renderMap = () => (
    <GoogleMap
      mapContainerStyle={customStyles ?? containerStyle}
      center={center}
      zoom={zoom ?? 14}
      clickableIcons
    >
      {originLatLng && (
        <CircleF
          center={originLatLng}
          radius={50}
          options={{
            strokeColor: radiusColors,
            strokeOpacity: 1,
            strokeWeight: 1,
            fillColor: radiusColors,
            fillOpacity: 0.15,
            clickable: false,
            draggable: false,
            editable: false,
            zIndex: 1,
          }}
        />
      )}

      {multipleMarkers ? (
        multipleMarkers.map((marker, i) => (
          <MarkerF
            key={`${marker.lat}-${marker.lng}-${i}`}
            onClick={() => handleActiveMarker(marker)}
            {...(!marker.name.includes('Origin') && {
              onMouseOver: () => handleMarkerMouseOver(marker),
              onMouseOut: () => handleMarkerMouseOut(),
            })}
            title={marker?.name || 'No Name'}
            position={{
              lat: marker?.lat,
              lng: marker?.lng,
            }}
            icon={{
              anchor: new window.google.maps.Point(32, 32),
              scaledSize: new window.google.maps.Size(34, 34),
              url: marker?.markerImage,
            }}
          >
            {activeMarker?.lat === marker?.lat && (
              <InfoWindowF
                anchor={marker}
                position={{
                  lat: marker?.lat,
                  lng: marker?.lng,
                }}
                onCloseClick={() => setActiveMarker(null)}
                options={{
                  headerContent,
                  content,
                }}
              >
                <></>
              </InfoWindowF>
            )}
          </MarkerF>
        ))
      ) : (
        <MarkerF position={center} />
      )}
      {hoverDistance && (
        <div style={hoverDistanceStyle}>
          Distance from Anchor:
          <span className="font-bold">{` ${hoverDistance}`}</span>
        </div>
      )}
    </GoogleMap>
  );

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  return isLoaded ? renderMap() : <Loader height={35} />;
}

export default React.memo(GoogleMapComponent);
